import {Injectable} from '@angular/core';
import {NavigationId} from '@application/helper/routing/navigation-id.enum';
import {NavigationUtils} from '@application/helper/routing/navigation-utils';
import {MachineType} from '@domain/machine/machine-type.enum';
import {
  AssertionUtils,
  ColDefBuilderFactoryService,
  CrudColdef,
  GridOptionsBuilder,
  NoDataOverlayComponentParams,
  OverlayComponentParams,
  StringUtils,
  TranslateService
} from '@vdw/angular-component-library';
import {CellRendererSelectorResult, ColDef, ICellRendererParams, ValueGetterParams} from 'ag-grid-community';

@Injectable({providedIn: 'root'})
export class MachineTypeOverviewService {
  private readonly additionalMachineTypes: string[] = ['BOBBIN_HOLDER_SHAFT', 'EXTRUSION_WINDER'];
  private readonly machineTypeValues: string[] = Object.keys(MachineType).concat(this.additionalMachineTypes);

  public constructor(
    private readonly colDefBuilderFactoryService: ColDefBuilderFactoryService,
    private readonly translate: TranslateService
  ) {}

  public getColumnDefs(): CrudColdef {
    return {
      colDefs: [
        this.colDefBuilderFactoryService
          .getBuilder()
          .withColIdAndField('machineType')
          .withRowGroup()
          .withHide(true)
          .withValueGetter((params: ValueGetterParams) => this.translate.instant(`MACHINE.TYPES.${params.data.machineType.toUpperCase()}`))
          .build(),
        this.colDefBuilderFactoryService.getBuilder().withHeaderName('UTILITIES.MACHINE_TYPE.MANUFACTURER').withColIdAndField('manufacturer.name').withComparator(StringUtils.stringComparator).build(),
        this.colDefBuilderFactoryService.getBuilder().withHeaderName('UTILITIES.MACHINE_TYPE.MODEL').withColIdAndField('model', true).withComparator(StringUtils.stringComparator).build()
      ]
    } as CrudColdef;
  }

  public getDefaultGridOptionsBuilder(builder: GridOptionsBuilder): GridOptionsBuilder {
    return builder
      .withLoadingOverlay({
        scale: 0.7
      } as OverlayComponentParams)
      .withNoRowsOverlay({
        scale: 0.7,
        titleParam: this.translate.instant(NavigationUtils.getNavigationData(NavigationId.MACHINE_TYPE_MODULE).navigationKey, {count: 1}),
        hideDescription: true
      } as NoDataOverlayComponentParams);
  }

  public getAutoGroupColumnDef(): ColDef {
    const translatedMachineTypeValues: string[] = this.machineTypeValues.map((machineTypeValue: string) => {
      return this.translate.instant(`MACHINE.TYPES.${machineTypeValue.toUpperCase()}`);
    });

    return this.colDefBuilderFactoryService
      .getBuilder()
      .withCellRendererSelector((params: ICellRendererParams): CellRendererSelectorResult => {
        if (
          params.node.allLeafChildren &&
          params.node.allLeafChildren.length === 1 &&
          translatedMachineTypeValues.includes(params.node.allLeafChildren[0].data?.name) &&
          params.node.allLeafChildren[0].data?.manufacturer?.id === 0 &&
          AssertionUtils.isNullOrUndefined(params.node.allLeafChildren[0].data?.manufacturer?.name) &&
          AssertionUtils.isNullOrUndefined(params.node.allLeafChildren[0].data?.model)
        ) {
          return null;
        }
        return {component: 'agGroupCellRenderer', params: {suppressCount: true}};
      })
      .withHeaderName('GENERAL.NAME')
      .withField('name')
      .withSuppressMovable()
      .build();
  }
}
